import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Send, Copy, Globe, Crop } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { refreshBranding } from "@/lib/branding";
import { refreshAppTz, listIanaZones, tzAbbr, buildTimezoneGroups, cityExamplesFor } from "@/lib/tz";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ImageCropDialog from "@/components/ImageCropDialog";
import PwaReadinessPanel from "@/components/PwaReadinessPanel";

const BRAND_CROP_CONFIG = {
  logo: { field: "logo_path", sourceField: "logo_source_path", title: "Crop primary SLX logo", defaultAspect: "original", defaultFitMode: "contain", defaultLongEdge: 1800 },
  login_logo: { field: "login_logo_path", sourceField: "login_logo_source_path", title: "Crop login-screen logo", defaultAspect: "original", defaultFitMode: "contain", defaultLongEdge: 1800 },
  crew_logo: { field: "crew_logo_path", sourceField: "crew_logo_source_path", title: "Crop Crew Portal logo", defaultAspect: "original", defaultFitMode: "contain", defaultLongEdge: 1800 },
  admin_logo: { field: "admin_logo_path", sourceField: "admin_logo_source_path", title: "Crop Admin dashboard logo", defaultAspect: "original", defaultFitMode: "contain", defaultLongEdge: 1800 },
  pwa_icon: { field: "pwa_icon_path", sourceField: "pwa_icon_source_path", title: "Crop home-screen / PWA icon", lockAspect: 1, defaultAspect: "square", defaultFitMode: "contain", defaultLongEdge: 1024, safeArea: true },
  ipad_icon: { field: "ipad_icon_path", sourceField: "ipad_icon_source_path", title: "Crop cabin iPad home-screen icon", lockAspect: 1, defaultAspect: "square", defaultFitMode: "contain", defaultLongEdge: 1024, safeArea: true },
};

export default function SettingsPanel() {
  const { admin } = useAuth();
  const isDeveloper = admin?.role === "developer";
  const [form, setForm] = useState({
    spa_name: "",
    boat_name: "",
    global_min_notice_min: 120,
    prep_time_min: 15,
    open_time: "09:00",
    close_time: "20:00",
    logo_path: null,
    logo_source_path: null,
    login_logo_path: null,
    login_logo_source_path: null,
    crew_logo_path: null,
    crew_logo_source_path: null,
    admin_logo_path: null,
    admin_logo_source_path: null,
    hero_image_path: null,
    pwa_icon_path: null,
    pwa_icon_source_path: null,
    ipad_icon_path: null,
    ipad_icon_source_path: null,
    emails_enabled: true,
    email_from_name: "",
    email_reply_to: "",
    notify_emails: [],
    reminder_min_before: 60,
    smtp_enabled: false,
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    smtp_use_ssl: false,
    smtp_from_address: "",
    timezone: "UTC",
    dev_banner_enabled: false,
    dev_banner_text: "We're still fine-tuning this app — please let us know if you spot anything odd. Your feedback helps us make it perfect for you.",
    priority_claim_enabled: false,
    priority_claim_prompt: false,
    adults_only_message: "",
    spa_door_identity_mode: "full_name",
    spa_door_logo_url: "",
    spa_door_logo_position: "top-center",
    spa_door_logo_size: 120,
  });
  const [newNotifyEmail, setNewNotifyEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, login_logo: false, crew_logo: false, admin_logo: false, hero: false, pwa_icon: false, ipad_icon: false, spa_ops_hero: false, pantry_hero: false });
  const [iconCrop, setIconCrop] = useState(null); // { kind, file, fromExisting } — independent per branding field

  useEffect(() => {
    (async () => { const { data } = await api.get("/admin/settings"); setForm((f) => ({ ...f, ...data, notify_emails: data.notify_emails || [] })); })();
  }, []);

  const uploadFileOnly = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  // Direct image uploads are still used for non-logo artwork such as hero
  // backgrounds. Every logo/icon goes through ImageCropDialog below.
  const uploadImage = async (kind, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const path = await uploadFileOnly(file);
      const fieldMap = { hero: "hero_image_path", spa_ops_hero: "spa_ops_hero_image_path", pantry_hero: "pantry_hero_image_path" };
      if (!fieldMap[kind]) throw new Error(`Unsupported direct image field: ${kind}`);
      setForm((f) => ({ ...f, [fieldMap[kind]]: path }));
      toast.success("Uploaded");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message || "Upload failed"); }
    finally { setUploading((u) => ({ ...u, [kind]: false })); }
  };

  const beginBrandCrop = (kind, file, fromExisting = false) => {
    if (!file || !BRAND_CROP_CONFIG[kind]) return;
    setIconCrop({ kind, file, fromExisting });
  };

  const recropExistingBrand = async (kind) => {
    const cfg = BRAND_CROP_CONFIG[kind];
    if (!cfg) return;
    const path = form[cfg.sourceField] || form[cfg.field];
    if (!path) return;
    try {
      const res = await fetch(fileUrl(path), { credentials: "include", cache: "no-store" });
      if (!res.ok) throw new Error("Could not load original branding image");
      const blob = await res.blob();
      const ext = blob.type === "image/svg+xml" ? "svg" : blob.type === "image/webp" ? "webp" : blob.type === "image/jpeg" ? "jpg" : "png";
      beginBrandCrop(kind, new File([blob], `${kind}-source.${ext}`, { type: blob.type || "image/png" }), true);
    } catch (_e) { toast.error("Could not load the original image for re-cropping"); }
  };

  const commitBrandCrop = async (croppedFile) => {
    if (!iconCrop?.kind) return;
    const kind = iconCrop.kind;
    const cfg = BRAND_CROP_CONFIG[kind];
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      // Keep the uncropped source on first upload. Re-cropping uses this source
      // forever, so repeated edits never lose artwork outside a previous crop.
      let sourcePath = form[cfg.sourceField] || null;
      const croppedPath = await uploadFileOnly(croppedFile);
      if (!iconCrop.fromExisting || !sourcePath) {
        // The backend intentionally does not store raw SVG for security. SVG
        // can still be selected and rendered by the crop studio; for those
        // sources we retain the rendered PNG as the future re-crop source.
        const canStoreOriginal = /\.(png|jpe?g|webp|gif)$/i.test(iconCrop.file?.name || "") && iconCrop.file?.type !== "image/svg+xml";
        sourcePath = canStoreOriginal ? await uploadFileOnly(iconCrop.file) : croppedPath;
      }
      setForm((f) => ({ ...f, [cfg.field]: croppedPath, [cfg.sourceField]: sourcePath }));
      toast.success("Crop applied");
      setIconCrop(null);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not upload cropped image");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const clearBrandImage = (kind) => {
    const cfg = BRAND_CROP_CONFIG[kind];
    if (!cfg) return;
    setForm((f) => ({ ...f, [cfg.field]: null, [cfg.sourceField]: null }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        global_min_notice_min: Number(form.global_min_notice_min) || 0,
        prep_time_min: Number(form.prep_time_min) || 0,
        reminder_min_before: Number(form.reminder_min_before) || 0,
        smtp_port: Number(form.smtp_port) || 587,
        spa_door_logo_size: Math.max(48, Math.min(Number(form.spa_door_logo_size) || 120, 320)),
        notify_emails: (form.notify_emails || []).filter((e) => e && e.includes("@")),
      };
      await api.put("/admin/settings", payload);
      await refreshBranding();
      await refreshAppTz();
      toast.success("Settings saved");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Could not save settings"); }
    finally { setSaving(false); }
  };

  const addNotifyEmail = () => {
    const v = (newNotifyEmail || "").trim();
    if (!v || !v.includes("@")) { toast.error("Enter a valid email"); return; }
    if ((form.notify_emails || []).includes(v)) { setNewNotifyEmail(""); return; }
    setForm({ ...form, notify_emails: [...(form.notify_emails || []), v] });
    setNewNotifyEmail("");
  };
  const removeNotifyEmail = (v) => setForm({ ...form, notify_emails: (form.notify_emails || []).filter((e) => e !== v) });

  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [emailErrDialog, setEmailErrDialog] = useState(null); // { detail: string }

  const showEmailError = (detail) => {
    const msg = detail || "Email failed";
    // Long multi-line diagnostics (>140 chars or newline) get a persistent
    // dialog so the operator can actually read the M365 remediation checklist.
    if (msg.length > 140 || msg.includes("\n")) {
      setEmailErrDialog({ detail: msg });
    } else {
      toast.error(msg, { duration: 8000 });
    }
  };

  const sendTest = async () => {
    const to = (testEmail || "").trim();
    if (!to || !to.includes("@")) { toast.error("Enter a valid email to send the test to"); return; }
    setTesting(true);
    try {
      // Auto-save the current SMTP form BEFORE sending the test — the test-email
      // endpoint reads from the database, not the browser state, so a user who
      // tweaked host/username/password but forgot to press Save would otherwise
      // silently hit the OLD saved config. This eliminates the "the server is
      // sending as someone else / feels hardcoded" bug class.
      const payload = {
        ...form,
        global_min_notice_min: Number(form.global_min_notice_min) || 0,
        prep_time_min: Number(form.prep_time_min) || 0,
        reminder_min_before: Number(form.reminder_min_before) || 0,
        smtp_port: Number(form.smtp_port) || 587,
        spa_door_logo_size: Math.max(48, Math.min(Number(form.spa_door_logo_size) || 120, 320)),
        notify_emails: (form.notify_emails || []).filter((e) => e && e.includes("@")),
      };
      try {
        await api.put("/admin/settings", payload);
      } catch (saveErr) {
        showEmailError(
          "Could not save your current SMTP settings before the test. " +
          "Fix the field-level error and save, then retry the test.\n\n" +
          (formatErr(saveErr.response?.data?.detail) || "Unknown save error")
        );
        return;
      }
      const { data } = await api.post("/admin/settings/test-email", { recipient: to });
      // Endpoint always returns HTTP 200 with { ok, detail?, transport, recipient }
      // so Cloudflare doesn't intercept a 5xx body with its own HTML error page.
      if (data && data.ok === false) {
        showEmailError(data.detail);
      } else {
        toast.success(`Test sent to ${data.recipient}`);
      }
    } catch (err) {
      showEmailError(formatErr(err.response?.data?.detail) || "Test send failed");
    } finally { setTesting(false); }
  };

  const copyErrToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailErrDialog?.detail || "");
      toast.success("Diagnostic copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="max-w-4xl">
      <ImageCropDialog
        open={!!iconCrop}
        onOpenChange={(v) => { if (!v) setIconCrop(null); }}
        file={iconCrop?.file || null}
        title={iconCrop?.kind ? BRAND_CROP_CONFIG[iconCrop.kind]?.title : "Crop image"}
        lockAspect={iconCrop?.kind ? BRAND_CROP_CONFIG[iconCrop.kind]?.lockAspect : null}
        defaultAspect={iconCrop?.kind ? BRAND_CROP_CONFIG[iconCrop.kind]?.defaultAspect : "original"}
        defaultFitMode={iconCrop?.kind ? BRAND_CROP_CONFIG[iconCrop.kind]?.defaultFitMode : "contain"}
        defaultLongEdge={iconCrop?.kind ? BRAND_CROP_CONFIG[iconCrop.kind]?.defaultLongEdge : 1600}
        safeArea={!!(iconCrop?.kind && BRAND_CROP_CONFIG[iconCrop.kind]?.safeArea)}
        onConfirm={commitBrandCrop}
      />
      <h2 className="font-display text-3xl mb-2">Settings & Branding</h2>
      <p className="text-sm text-muted-foreground mb-8">Customize the boat name, logo, hero background, and reservation rules.</p>

      <div className="space-y-8 bg-card border border-border p-8">
        {/* Advisory banner — a polite pre-launch note the operator can
            flash across every route (guest landing, iPad cabin, admin,
            crew). Off by default; text is optional but a friendly seed
            is provided so the operator can just flip it on and go. */}
        <div className="border-l-2 border-amber-500/60 pl-4">
          <div className="overline text-primary/60 mb-3">Advisory banner</div>
          <div className="flex items-center gap-3 mb-3">
            <Switch
              checked={!!form.dev_banner_enabled}
              onCheckedChange={(v) => setForm({ ...form, dev_banner_enabled: v })}
              data-testid="sf-dev-banner-enabled"
            />
            <Label className="cursor-pointer" onClick={() => setForm({ ...form, dev_banner_enabled: !form.dev_banner_enabled })}>
              Show the banner across the whole app
            </Label>
          </div>
          <div>
            <Label className="overline text-primary/60 text-[10px]">Message</Label>
            <Textarea
              value={form.dev_banner_text || ""}
              onChange={(e) => setForm({ ...form, dev_banner_text: e.target.value })}
              placeholder="We're still fine-tuning this app — please let us know if you spot anything odd."
              className="mt-2 min-h-[64px]"
              data-testid="sf-dev-banner-text"
              disabled={!form.dev_banner_enabled}
            />
            <p className="text-xs text-muted-foreground mt-2">
              A slim advisory bar shown at the very top of every page — landing, guest menu,
              cabin iPad, admin dashboard, crew view. Guests can dismiss it for their session;
              when you edit the message, dismissed devices re-show it automatically.
            </p>
          </div>
        </div>

        {/* Priority-claim service — two switches per user's spec. Master
            activates the whole feature; the secondary switch controls
            whether primaries get a polite notification every time a
            non-primary books. When both are off, the feature is dormant. */}
        <div className="border-l-2 border-amber-500/60 pl-4">
          <div className="overline text-primary/60 mb-3">Primary-guest priority claim</div>
          <div className="flex items-center gap-3 mb-2">
            <Switch
              checked={!!form.priority_claim_enabled}
              onCheckedChange={(v) => setForm({ ...form, priority_claim_enabled: v, priority_claim_prompt: v ? form.priority_claim_prompt : false })}
              data-testid="sf-priority-claim-enabled"
            />
            <Label className="cursor-pointer" onClick={() => setForm({ ...form, priority_claim_enabled: !form.priority_claim_enabled })}>
              Activate the priority-claim service
            </Label>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1 mb-3 ml-11 leading-snug">
            When on, primary guests can pre-empt any regular booking. Bumped guests get an automatic courtesy notice.
          </p>
          <div className="flex items-center gap-3 mb-2">
            <Switch
              checked={!!form.priority_claim_prompt}
              onCheckedChange={(v) => setForm({ ...form, priority_claim_prompt: v })}
              disabled={!form.priority_claim_enabled}
              data-testid="sf-priority-claim-prompt"
            />
            <Label className={`cursor-pointer ${!form.priority_claim_enabled ? "text-muted-foreground" : ""}`} onClick={() => form.priority_claim_enabled && setForm({ ...form, priority_claim_prompt: !form.priority_claim_prompt })}>
              Notify primaries when a non-primary books
            </Label>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1 ml-11 leading-snug">
            Sends a polite in-app notification: "Guest X booked at 14:00. Do you want to priority-claim this timeslot?"
            Primaries can still claim proactively when this is off.
          </p>
        </div>

        <div>
          <div className="overline text-primary/60 mb-4">Branding</div>
          <div className="space-y-4">
            <div>
              <Label className="overline text-primary/60">Boat name</Label>
              <Input value={form.boat_name || ""} onChange={(e) => setForm({ ...form, boat_name: e.target.value })} data-testid="sf-boat-name" className="mt-2" placeholder="e.g. Carinthia VII" />
            </div>
            <div>
              <Label className="overline text-primary/60">Spa name</Label>
              <Input value={form.spa_name || ""} onChange={(e) => setForm({ ...form, spa_name: e.target.value })} data-testid="sf-spa-name" className="mt-2" />
              <p className="text-xs text-muted-foreground mt-1">Shown in the header and menu.</p>
            </div>

            <div>
              <Label className="overline text-primary/60">Logo</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.logo_path ? (
                  <div className="relative">
                    <img src={fileUrl(form.logo_path)} alt="Logo" className="w-16 h-16 object-contain bg-muted border border-border p-2" />
                    <button
                      onClick={() => clearBrandImage("logo")}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      data-testid="sf-logo-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-16 h-16 border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">None</div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.logo ? "Uploading…" : "Upload logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => beginBrandCrop("logo", e.target.files?.[0])} data-testid="sf-logo-upload" />
                </label>
                {form.logo_path && <Button type="button" variant="outline" size="sm" onClick={() => recropExistingBrand("logo")}><Crop className="h-4 w-4 mr-2"/>Crop current</Button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Shown in the guest header and admin sidebar. Transparent PNG recommended.</p>
            </div>

            <div>
              <Label className="overline text-primary/60">Login-screen logo</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.login_logo_path ? (
                  <div className="relative">
                    <img src={fileUrl(form.login_logo_path)} alt="Login logo" className="w-16 h-16 object-contain bg-muted border border-border p-2" data-testid="sf-login-logo-preview" />
                    <button
                      onClick={() => clearBrandImage("login_logo")}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      data-testid="sf-login-logo-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-16 h-16 border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground text-center leading-tight px-1">
                    Falls back<br />to logo
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.login_logo ? "Uploading…" : "Upload login logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => beginBrandCrop("login_logo", e.target.files?.[0])} data-testid="sf-login-logo-upload" />
                </label>
                {form.login_logo_path && <Button type="button" variant="outline" size="sm" onClick={() => recropExistingBrand("login_logo")}><Crop className="h-4 w-4 mr-2"/>Crop current</Button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Shown ONLY on the pre-sign-in landing (Guest / iPad / Crew tabs) and the admin login page.
                Great for a full-colour hero mark on arrival while the compact logo above stays as the everyday interface mark.
                Falls back to the main logo when unset.
              </p>
            </div>

            <div>
              <Label className="overline text-primary/60">Crew Portal logo</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.crew_logo_path ? (
                  <div className="relative">
                    <img src={fileUrl(form.crew_logo_path)} alt="Crew Portal logo" className="w-16 h-16 object-contain bg-muted border border-border p-2" data-testid="sf-crew-logo-preview" />
                    <button
                      onClick={() => clearBrandImage("crew_logo")}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      data-testid="sf-crew-logo-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-16 h-16 border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground text-center leading-tight px-1">
                    Falls back<br />to logo
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.crew_logo ? "Uploading…" : "Upload crew logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => beginBrandCrop("crew_logo", e.target.files?.[0])} data-testid="sf-crew-logo-upload" />
                </label>
                {form.crew_logo_path && <Button type="button" variant="outline" size="sm" onClick={() => recropExistingBrand("crew_logo")}><Crop className="h-4 w-4 mr-2"/>Crop current</Button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Shown in the top-bar of the <span className="font-medium text-foreground">Crew Portal</span> — the mobile / iPhone-first
                dashboard opened via <code className="text-[11px]">/crew</code>. Lets you carry a stripped-back, staff-only mark distinct
                from the guest spa logo. Falls back to the main logo when unset.
              </p>
            </div>

            <div>
              <Label className="overline text-primary/60">Admin dashboard logo</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.admin_logo_path ? (
                  <div className="relative">
                    <img src={fileUrl(form.admin_logo_path)} alt="Admin sidebar logo" className="w-16 h-16 object-contain bg-muted border border-border p-2" data-testid="sf-admin-logo-preview" />
                    <button
                      onClick={() => clearBrandImage("admin_logo")}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      data-testid="sf-admin-logo-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-16 h-16 border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground text-center leading-tight px-1">
                    Falls back<br />to logo
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.admin_logo ? "Uploading…" : "Upload admin logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => beginBrandCrop("admin_logo", e.target.files?.[0])} data-testid="sf-admin-logo-upload" />
                </label>
                {form.admin_logo_path && <Button type="button" variant="outline" size="sm" onClick={() => recropExistingBrand("admin_logo")}><Crop className="h-4 w-4 mr-2"/>Crop current</Button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Shown ONLY in the admin dashboard sidebar (the desktop management UI). Lets you carry a
                backstage mark that's distinct from the guest spa header — helpful when the same yacht
                runs a subtle admin brand vs. a full-colour guest brand. Falls back to the main logo when unset.
              </p>
            </div>


            <PwaReadinessPanel />

            <div>
              <Label className="overline text-primary/60">Home-screen / PWA icon</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.pwa_icon_path ? (
                  <div className="relative">
                    {/* Preview roughly the way iOS / Android render it:
                        square, gold background, safe-area padding. */}
                    <div
                      className="w-16 h-16 flex items-center justify-center"
                      style={{ background: form.theme_accent || "#B5A78A", borderRadius: 14 }}
                    >
                      <img src={fileUrl(form.pwa_icon_path)} alt="PWA icon" className="w-11 h-11 object-contain" />
                    </div>
                    <button
                      onClick={() => clearBrandImage("pwa_icon")}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      data-testid="sf-pwa-icon-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-16 h-16 border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground text-center leading-tight px-1">
                    Falls back<br />to logo
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.pwa_icon ? "Uploading…" : "Upload icon"}
                  <input
                    type="file"
                    accept="image/png,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={(e) => beginBrandCrop("pwa_icon", e.target.files?.[0])}
                    data-testid="sf-pwa-icon-upload"
                  />
                </label>
                {form.pwa_icon_path && <Button type="button" variant="outline" size="sm" onClick={() => recropExistingBrand("pwa_icon")}><Crop className="h-4 w-4 mr-2"/>Crop current</Button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Square PNG or SVG for the "Add to Home Screen" install.
                Recommended: <strong>512 × 512, transparent background</strong>,
                with the artwork filling ~70 % of the frame so nothing gets clipped by iOS's rounded-corner mask.
                Falls back to the main logo when unset.
              </p>
            </div>

            <div>
              <Label className="overline text-primary/60">Cabin iPad home-screen icon</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.ipad_icon_path ? (
                  <div className="relative">
                    <div className="w-16 h-16 flex items-center justify-center bg-black/5 border border-border">
                      <img src={fileUrl(form.ipad_icon_path)} alt="iPad icon" className="w-11 h-11 object-contain" />
                    </div>
                    <button
                      onClick={() => clearBrandImage("ipad_icon")}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center"
                      data-testid="sf-ipad-icon-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-16 h-16 border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground text-center leading-tight px-1">
                    Falls back<br />to PWA icon
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.ipad_icon ? "Uploading…" : "Upload iPad icon"}
                  <input
                    type="file"
                    accept="image/png,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={(e) => beginBrandCrop("ipad_icon", e.target.files?.[0])}
                    data-testid="sf-ipad-icon-upload"
                  />
                </label>
                {form.ipad_icon_path && <Button type="button" variant="outline" size="sm" onClick={() => recropExistingBrand("ipad_icon")}><Crop className="h-4 w-4 mr-2"/>Crop current</Button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Home-screen icon shown ONLY when the guest cabin iPad is signed in (kiosk mode).
                Lets you brand cabin iPads distinctly from personal-phone guests — e.g. a monogram vs the full spa mark.
                Same 512 × 512 transparent PNG guidance as the main PWA icon.
                Falls back to the main PWA icon when unset.
              </p>
            </div>


            <div>
              <Label className="overline text-primary/60">Hero background</Label>
              <div className="flex items-start gap-4 mt-2">
                {form.hero_image_path ? (
                  <div className="relative">
                    <img src={fileUrl(form.hero_image_path)} alt="Hero" className="w-40 h-24 object-cover border border-border" />
                    <button
                      onClick={() => setForm({ ...form, hero_image_path: null })}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      data-testid="sf-hero-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-40 h-24 border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">Default</div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.hero ? "Uploading…" : "Upload hero"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage("hero", e.target.files?.[0])} data-testid="sf-hero-upload" />
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Full-bleed image on the guest landing page. Landscape 16:9 recommended.</p>
            </div>

            {/* Kiosk hero backgrounds — one per shared iPad role. Rendered
                at 18 % opacity behind the Spa Ops / Pantry views so the
                two kiosks are visually distinct at a glance. Empty = the
                view falls back to the plain neutral bg. */}
            <div>
              <Label className="overline text-primary/60">Spa Ops kiosk background</Label>
              <div className="flex items-start gap-4 mt-2">
                {form.spa_ops_hero_image_path ? (
                  <div className="relative">
                    <img src={fileUrl(form.spa_ops_hero_image_path)} alt="Spa Ops hero" className="w-40 h-24 object-cover border border-border" />
                    <button
                      onClick={() => setForm({ ...form, spa_ops_hero_image_path: null })}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      data-testid="sf-spa-ops-hero-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-40 h-24 border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">Default</div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.spa_ops_hero ? "Uploading…" : "Upload Spa Ops"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage("spa_ops_hero", e.target.files?.[0])} data-testid="sf-spa-ops-hero-upload" />
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Shown behind the Spa Ops kiosk view (18 % opacity). Landscape 16:9 recommended.</p>
            </div>

            <div>
              <Label className="overline text-primary/60">Pantry kiosk background</Label>
              <div className="flex items-start gap-4 mt-2">
                {form.pantry_hero_image_path ? (
                  <div className="relative">
                    <img src={fileUrl(form.pantry_hero_image_path)} alt="Pantry hero" className="w-40 h-24 object-cover border border-border" />
                    <button
                      onClick={() => setForm({ ...form, pantry_hero_image_path: null })}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      data-testid="sf-pantry-hero-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-40 h-24 border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">Default</div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploading.pantry_hero ? "Uploading…" : "Upload Pantry"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage("pantry_hero", e.target.files?.[0])} data-testid="sf-pantry-hero-upload" />
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Shown behind the Pantry kiosk view (18 % opacity). Landscape 16:9 recommended.</p>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-8">
          <div className="overline text-primary/60 mb-4">Time & locale</div>
          <div className="space-y-4">
            <div className="border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
              Wellness reservation rules and Spa Door settings have moved to <strong className="text-foreground">Modules → SPA & Wellness → Module settings</strong>.
            </div>
            <div className="pt-2">
              <Label className="overline text-primary/60 flex items-center gap-2">
                <Globe className="h-3 w-3" /> Yacht timezone <span className="ml-2 normal-case tracking-normal text-[10px] text-muted-foreground">(currently {tzAbbr(form.timezone || "UTC")} · {form.timezone || "UTC"})</span>
              </Label>
              {/* Grouped GMT-offset dropdown — every IANA zone bucketed by
                  its current UTC offset, cities alphabetised within each
                  bucket. Rebuilt on every render so DST transitions are
                  reflected without a refresh. Falls back gracefully on
                  browsers without Intl.supportedValuesOf via listIanaZones. */}
              <select
                value={form.timezone || "UTC"}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                data-testid="sf-timezone"
                className="mt-2 w-full h-10 px-3 border border-border bg-background text-sm font-mono"
              >
                {buildTimezoneGroups().map((g) => (
                  <optgroup key={g.gmt} label={`${g.gmt}  ·  ${cityExamplesFor(g.zones)}`}>
                    {g.zones.map((z) => (
                      <option key={z.tz} value={z.tz}>{z.tz.replace(/_/g, " ")}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                All times are stored in UTC. Zones are grouped by <span className="font-mono">GMT±HH:MM</span> with example cities so you can pick by region at a glance. Change this whenever the yacht cruises to a new timezone — upcoming bookings re-render in the new zone. Bookings that already ended stay in their original zone.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-8">
          {!isDeveloper ? (
            <div className="border border-border bg-muted/30 p-4" data-testid="email-dev-only-notice">
              <div className="overline text-primary/60 mb-1">Email notifications</div>
              <p className="text-xs text-muted-foreground">
                Email configuration (sender, SMTP, reminders, notify list, test send) is managed
                by a <strong>Developer</strong> user in <em>Platform tools</em>.
              </p>
            </div>
          ) : (
          <>
          <div className="overline text-primary/60 mb-4">Email notifications <span className="text-[10px] normal-case tracking-normal text-accent ml-2">Developer only</span></div>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.emails_enabled}
                onChange={(e) => setForm({ ...form, emails_enabled: e.target.checked })}
                data-testid="sf-emails-enabled"
                className="h-4 w-4 accent-primary"
              />
              <span className="text-sm font-medium">Send emails <span className="text-muted-foreground text-xs">(master switch — off silences ALL notifications)</span></span>
            </label>

            {/* Per-event kill switches. Each defaults ON so upgrading yachts
                keep the previous confirmation+reminder behaviour and can
                opt-in to the new cancel/reschedule/priority-claim notices. */}
            <div className={`border-l-2 border-accent/30 pl-5 py-2 space-y-3 ${form.emails_enabled ? "" : "opacity-50 pointer-events-none"}`} data-testid="sf-email-events">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Notify guests &amp; crew when…</div>
              {[
                { key: "email_on_booking_confirm", label: "A new booking is created",       hint: "Guest gets the confirmation. Everyone on the Notify list gets an ops copy." },
                { key: "email_on_booking_reminder", label: "A reminder is due",              hint: "Sent to the guest only, N minutes before start_at (set below)." },
                { key: "email_on_booking_cancel",   label: "A booking is cancelled",         hint: "Guest + Notify list. Fires on both status→cancelled and hard-deletes." },
                { key: "email_on_booking_reschedule", label: "A booking is rescheduled",     hint: "Guest + Notify list. Sent when start_at changes." },
                { key: "email_on_priority_claim_bump", label: "A primary guest bumps a slot", hint: "Bumped guest gets a courteous notice with the treatment + old time." },
              ].map((row) => (
                <label key={row.key} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={form[row.key] !== false}
                    onChange={(e) => setForm({ ...form, [row.key]: e.target.checked })}
                    data-testid={`sf-${row.key.replace(/_/g, "-")}`}
                    className="h-4 w-4 accent-primary mt-0.5"
                  />
                  <span className="flex-1">
                    <span className="text-sm block leading-tight">{row.label}</span>
                    <span className="text-[11px] text-muted-foreground block mt-0.5">{row.hint}</span>
                  </span>
                </label>
              ))}
            </div>

            <div>
              <Label className="overline text-primary/60">Sender display name</Label>
              <Input
                value={form.email_from_name || ""}
                onChange={(e) => setForm({ ...form, email_from_name: e.target.value })}
                data-testid="sf-email-from-name"
                placeholder="e.g. Carinthia VII Spa"
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">Shown as the "From" name in guests' inbox. The From address is managed by the platform.</p>
            </div>

            <div>
              <Label className="overline text-primary/60">Reply-to address (optional)</Label>
              <Input
                type="email"
                value={form.email_reply_to || ""}
                onChange={(e) => setForm({ ...form, email_reply_to: e.target.value })}
                data-testid="sf-email-reply-to"
                placeholder="spa@yacht.com"
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">If guests reply, mail goes here.</p>
            </div>

            <div>
              <Label className="overline text-primary/60">Notify admin/crew emails on new bookings</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  value={newNotifyEmail}
                  onChange={(e) => setNewNotifyEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNotifyEmail(); } }}
                  placeholder="crew@yacht.com"
                  data-testid="sf-notify-input"
                />
                <Button type="button" variant="outline" onClick={addNotifyEmail} data-testid="sf-notify-add">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="sf-notify-list">
                {(form.notify_emails || []).length === 0 && (
                  <span className="text-xs text-muted-foreground">No inboxes — nobody will be notified.</span>
                )}
                {(form.notify_emails || []).map((e) => (
                  <span key={e} className="inline-flex items-center gap-2 px-3 h-8 border border-border bg-muted/40 text-sm">
                    {e}
                    <button
                      type="button"
                      onClick={() => removeNotifyEmail(e)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`sf-notify-remove-${e}`}
                    ><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <Label className="overline text-primary/60">Reminder — minutes before appointment</Label>
              <Input
                type="number"
                min="0"
                value={form.reminder_min_before ?? 60}
                onChange={(e) => setForm({ ...form, reminder_min_before: e.target.value })}
                data-testid="sf-reminder-min"
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">0 disables reminders. Runs once per booking, checked every minute.</p>
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <div>
                <Label className="overline text-accent">Your email server (SMTP)</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Use your own mailbox (Gmail, Office365, custom domain) to send booking emails from your own address.
                  When enabled, this replaces the platform's shared sender.
                </p>
                {!form.smtp_enabled && (
                  <div className="mt-3 border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs space-y-2" data-testid="smtp-config-hint">
                    <div><strong className="text-amber-700 dark:text-amber-400">Recommended:</strong> enable your own SMTP below. The platform's shared sender is unreliable in this environment and often lands in spam.</div>
                    <div className="pt-1">
                      <strong className="text-foreground">Microsoft 365 (shared mailbox):</strong>
                      <ul className="list-disc pl-4 mt-1 space-y-0.5 text-[11px]">
                        <li>Host <span className="font-mono">smtp.office365.com</span> · Port <span className="font-mono">587</span> · TLS <em>on</em> · SSL <em>off</em></li>
                        <li><em>Username</em> = a <strong>licensed</strong> M365 user (has an active mailbox)</li>
                        <li><em>Password</em> = an App Password for that user (or normal password if AUTH is enabled)</li>
                        <li><em>From address</em> = the <strong>shared mailbox</strong> the licensed user has <span className="font-mono">Send&nbsp;As</span> permission on</li>
                        <li>Enable "Authenticated SMTP" on the licensed user's mailbox in the M365 admin centre if login fails with <span className="font-mono">535 5.7.139</span></li>
                      </ul>
                    </div>
                    <div className="pt-1"><strong className="text-foreground">Gmail:</strong> create an App password at <span className="font-mono">myaccount.google.com/apppasswords</span> and paste it in the password field.</div>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.smtp_enabled}
                  onChange={(e) => setForm({ ...form, smtp_enabled: e.target.checked })}
                  data-testid="sf-smtp-enabled"
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm">Send via my SMTP server</span>
              </label>

              {form.smtp_enabled && (
                <div className="space-y-4 pl-7 border-l-2 border-accent/30 pt-2">
                  {/* One-click provider presets — atomically set host + port + TLS/SSL
                      so users don't accidentally leave the host on Office365 while
                      typing a Gmail from-address (root cause of the 535 5.7.139
                      "basic authentication is disabled" mismatch). */}
                  <div>
                    <Label className="overline text-primary/60">Quick presets</Label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="sf-smtp-presets">
                      <button
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          smtp_host: "smtp.gmail.com",
                          smtp_port: 587,
                          smtp_use_tls: true,
                          smtp_use_ssl: false,
                        })}
                        className={`px-3 py-1.5 border text-xs uppercase tracking-widest transition-colors ${
                          (form.smtp_host || "").toLowerCase() === "smtp.gmail.com"
                            ? "border-accent bg-accent/10 text-accent"
                            : "border-border hover:border-accent/60 hover:text-accent"
                        }`}
                        data-testid="sf-smtp-preset-gmail"
                      >
                        Gmail
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          smtp_host: "smtp.office365.com",
                          smtp_port: 587,
                          smtp_use_tls: true,
                          smtp_use_ssl: false,
                        })}
                        className={`px-3 py-1.5 border text-xs uppercase tracking-widest transition-colors ${
                          (form.smtp_host || "").toLowerCase().includes("office365") || (form.smtp_host || "").toLowerCase().includes("outlook")
                            ? "border-accent bg-accent/10 text-accent"
                            : "border-border hover:border-accent/60 hover:text-accent"
                        }`}
                        data-testid="sf-smtp-preset-o365"
                      >
                        Office 365
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          smtp_host: "smtp-mail.outlook.com",
                          smtp_port: 587,
                          smtp_use_tls: true,
                          smtp_use_ssl: false,
                        })}
                        className={`px-3 py-1.5 border text-xs uppercase tracking-widest transition-colors ${
                          (form.smtp_host || "").toLowerCase() === "smtp-mail.outlook.com"
                            ? "border-accent bg-accent/10 text-accent"
                            : "border-border hover:border-accent/60 hover:text-accent"
                        }`}
                        data-testid="sf-smtp-preset-outlook"
                      >
                        Outlook.com
                      </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">One-click sets host, port and TLS — then just type your email + app password below.</p>
                  </div>

                  {/* Live mismatch banner — catches the "gmail address on office365 host"
                      class of mistakes before the user burns a test send. */}
                  {(() => {
                    const host = (form.smtp_host || "").toLowerCase();
                    const fromDomain = ((form.smtp_from_address || form.smtp_username || "").split("@")[1] || "").toLowerCase();
                    if (!host || !fromDomain) return null;
                    const hostIsGmail = host === "smtp.gmail.com";
                    const hostIsMs = host.includes("office365") || host.includes("outlook");
                    const fromIsGmail = fromDomain === "gmail.com" || fromDomain === "googlemail.com";
                    const fromIsMs = fromDomain === "outlook.com" || fromDomain === "hotmail.com" || fromDomain === "live.com";
                    let msg = null;
                    if (hostIsMs && fromIsGmail) {
                      msg = `Your from-address ends in "@${fromDomain}" (Gmail) but the host is set to a Microsoft server. Gmail won't authenticate against Microsoft's SMTP. Click the Gmail preset above.`;
                    } else if (hostIsGmail && fromIsMs) {
                      msg = `Your from-address ends in "@${fromDomain}" (Microsoft) but the host is set to Gmail. Click the Office 365 or Outlook.com preset above.`;
                    }
                    if (!msg) return null;
                    return (
                      <div className="border border-amber-500/60 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2" data-testid="sf-smtp-mismatch">
                        <span aria-hidden className="font-bold">!</span>
                        <span>{msg}</span>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="overline text-primary/60">SMTP host</Label>
                      <Input
                        value={form.smtp_host || ""}
                        onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                        placeholder="smtp.gmail.com"
                        data-testid="sf-smtp-host"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label className="overline text-primary/60">Port</Label>
                      <Input
                        type="number"
                        value={form.smtp_port ?? 587}
                        onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
                        placeholder="587"
                        data-testid="sf-smtp-port"
                        className="mt-2"
                      />
                      <p className="text-xs text-muted-foreground mt-1">587 for STARTTLS · 465 for SSL · 25 unencrypted (not recommended)</p>
                    </div>
                  </div>

                  <div>
                    <Label className="overline text-primary/60">From address</Label>
                    <Input
                      type="email"
                      value={form.smtp_from_address || ""}
                      onChange={(e) => {
                        // Auto-select a preset the moment the domain becomes
                        // recognisable — but ONLY if the host is empty (never
                        // stomp a value the operator already typed). Fixes
                        // the "typed a Gmail address but forgot to also
                        // switch the host from Office 365" class of bugs
                        // without annoying power users on custom SMTP.
                        const v = e.target.value;
                        const dom = ((v || "").split("@")[1] || "").toLowerCase();
                        const hostEmpty = !(form.smtp_host || "").trim();
                        let next = { ...form, smtp_from_address: v };
                        if (hostEmpty && dom) {
                          if (dom === "gmail.com" || dom === "googlemail.com") {
                            next = { ...next, smtp_host: "smtp.gmail.com", smtp_port: 587, smtp_use_tls: true, smtp_use_ssl: false };
                          } else if (dom === "outlook.com" || dom === "hotmail.com" || dom === "live.com") {
                            next = { ...next, smtp_host: "smtp-mail.outlook.com", smtp_port: 587, smtp_use_tls: true, smtp_use_ssl: false };
                          }
                          // We leave Office 365 tenant domains alone (org-specific)
                          // — the mismatch banner will prompt the O365 preset if the
                          // domain isn't public.
                        }
                        setForm(next);
                      }}
                      placeholder="spa@yachtdomain.com"
                      data-testid="sf-smtp-from"
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">The real "From:" email guests see. Must match your SMTP account for most providers. <span className="text-accent">Type a @gmail.com or @outlook.com address and we'll auto-pick the preset for you.</span></p>
                  </div>

                  <div>
                    <Label className="overline text-primary/60">SMTP username</Label>
                    <Input
                      value={form.smtp_username || ""}
                      onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
                      placeholder="Usually the same as the from address"
                      data-testid="sf-smtp-user"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label className="overline text-primary/60">SMTP password</Label>
                    <Input
                      type="password"
                      value={form.smtp_password || ""}
                      onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                      placeholder="App password (Gmail) or account password"
                      data-testid="sf-smtp-pass"
                      className="mt-2"
                      autoComplete="new-password"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      For Gmail: generate an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">App Password</a> (2-step verification required).
                      For Office365: enable "Authenticated SMTP" for the mailbox.
                    </p>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.smtp_use_tls}
                        onChange={(e) => setForm({ ...form, smtp_use_tls: e.target.checked, smtp_use_ssl: e.target.checked ? false : form.smtp_use_ssl })}
                        data-testid="sf-smtp-tls"
                        className="h-4 w-4 accent-primary"
                      />
                      STARTTLS (port 587)
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={!!form.smtp_use_ssl}
                        onChange={(e) => setForm({ ...form, smtp_use_ssl: e.target.checked, smtp_use_tls: e.target.checked ? false : form.smtp_use_tls })}
                        data-testid="sf-smtp-ssl"
                        className="h-4 w-4 accent-primary"
                      />
                      SSL (port 465)
                    </label>
                  </div>

                  <div className="bg-muted/50 border border-border p-3 text-xs text-muted-foreground">
                    <strong className="text-primary">Common presets:</strong><br />
                    <span className="font-mono">Gmail</span>: smtp.gmail.com · 587 · STARTTLS · use an App Password.<br />
                    <span className="font-mono">Office365</span>: smtp.office365.com · 587 · STARTTLS.<br />
                    <span className="font-mono">Custom</span>: check your provider's docs.
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-border pt-6">
              <Label className="overline text-primary/60">Send a test email</Label>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendTest(); } }}
                  placeholder="you@example.com"
                  data-testid="sf-test-email-input"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={sendTest}
                  disabled={testing}
                  className="border-primary/40 text-primary hover:bg-primary/5"
                  data-testid="sf-test-email-send"
                >
                  <Send className="h-3.5 w-3.5 mr-2" /> {testing ? "Sending…" : "Send test"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2" data-testid="sf-test-email-hint">
                Pressing <strong>Send test</strong> automatically saves your current SMTP settings first, then sends. No need to hit Save separately.
              </p>
            </div>
          </div>
          </>
          )}
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="sf-save">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>

      <Dialog open={!!emailErrDialog} onOpenChange={(open) => { if (!open) setEmailErrDialog(null); }}>
        <DialogContent className="max-w-2xl" data-testid="email-err-dialog">
          <DialogHeader>
            <DialogTitle>Email send failed</DialogTitle>
            <DialogDescription>
              Your mail server returned an error. The exact remediation steps are below — copy them to share with your M365 admin if needed.
            </DialogDescription>
          </DialogHeader>
          <div
            className="bg-muted/50 border border-border p-4 text-xs font-mono whitespace-pre-wrap max-h-[50vh] overflow-y-auto text-primary/90"
            data-testid="email-err-detail"
          >
            {emailErrDialog?.detail}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={copyErrToClipboard} data-testid="email-err-copy">
              <Copy className="h-3.5 w-3.5 mr-2" /> Copy
            </Button>
            <Button onClick={() => setEmailErrDialog(null)} data-testid="email-err-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
